import { Star, StarOff, X, ArrowUpRight, Image as ImageIcon, File, Copy } from "lucide-react"
import type { Message } from "./types"
import { copyTextToClipboard, copyableMessageText, hasCopyableText } from "./copy-text"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { AttachmentImg, AttachmentLink } from "./private-attachment-ui"

interface StarredMessagesPageProps {
  starredMessages: Message[]
  onUnstar: (message: Message) => void
  onJumpTo?: (messageId: string) => void
  onClose?: () => void
}

/**
 * When a starred message was sent, day included.
 *
 * The row showed the clock alone. In a thread that is enough — the messages
 * around it place it — but this list is the one place messages from different
 * days sit together, so "3:45 PM" could be this afternoon or last March and
 * there was nothing to tell them apart.
 *
 * Falls back to whatever the row already had if the timestamp is missing or
 * unparseable, rather than showing nothing.
 */
export function formatStarredWhen(createdAt: string | undefined, fallback: string): string {
  if (!createdAt) return fallback
  const d = new Date(createdAt)
  if (Number.isNaN(d.getTime())) return fallback

  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const now = new Date()
  const daysAgo = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000)

  if (daysAgo === 0) return `Today, ${time}`
  if (daysAgo === 1) return `Yesterday, ${time}`

  const sameYear = d.getFullYear() === now.getFullYear()
  const date = d.toLocaleDateString([], sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" })
  return `${date}, ${time}`
}

export function StarredMessagesPage({
  starredMessages,
  onUnstar,
  onJumpTo,
  onClose,
}: StarredMessagesPageProps) {
  const t = useTranslation()

  const handleCopy = async (message: Message) => {
    const ok = await copyTextToClipboard(copyableMessageText(message.text))
    if (ok) toast.success({ title: t("presence.messageCopied") })
    else toast.error({ title: t("presence.copyFailed") })
  }

  const getMessagePreview = (message: Message) => {
    // A caption, when there is one, says more than the attachment's kind.
    if (message.type !== "text" && message.text && !/^(📷|🎥|🎵|🎤|📄)/u.test(message.text)) {
      return message.text
    }
    switch (message.type) {
      case "image":
        return message.attachmentName ? `📷 ${message.attachmentName}` : "📷 Photo"
      case "video":
        return message.attachmentName ? `🎥 ${message.attachmentName}` : "🎥 Video"
      case "file":
        return message.attachmentName ? `📄 ${message.attachmentName}` : "📄 File"
      case "audio":
        return "🎵 Audio"
      case "call":
        return "📞 Call"
      default:
        return message.text
    }
  }

  const getMessageIcon = (message: Message) => {
    // The starred picture itself, not an icon standing in for it.
    const preview =
      message.type === "image" ? message.attachmentUrl
      : message.type === "video" || message.type === "file" ? message.attachmentThumbnailUrl
      : undefined
    if (preview) {
      return <AttachmentImg src={preview} alt={message.attachmentName ?? ""} loading="lazy" className="h-full w-full object-cover" />
    }
    switch (message.type) {
      case "image":
        return <ImageIcon className="w-4 h-4" />
      case "file":
        return <File className="w-4 h-4" />
      case "audio":
        return <span className="text-sm">🎵</span>
      case "call":
        return <span className="text-sm">📞</span>
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <h2 className="text-sm font-semibold text-foreground">Starred Messages</h2>
            <span className="text-xs bg-amber-400/10 text-amber-600 dark:text-amber-400 font-semibold px-2 py-0.5 rounded-full">{starredMessages.length}</span>
          </div>
        </div>
      </div>

      {/* Messages list */}
      <div className="flex-1 overflow-y-auto">
        {starredMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
            <Star className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-sm text-muted-foreground">No starred messages</p>
          </div>
        ) : (
          starredMessages.map(message => (
            <div
              key={message.id}
              className="flex gap-3 p-4 hover:bg-muted/50 cursor-pointer transition-colors border-b border-border/50 group"
            >
              <div
                className={`w-10 h-10 bg-muted flex items-center justify-center shrink-0 overflow-hidden ${
                  message.attachmentUrl && message.type !== "text" ? "rounded-lg" : "rounded-full"
                }`}
              >
                {getMessageIcon(message) || <span className="text-sm">💬</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-muted-foreground">{message.senderName}</span>
                  <span className="text-xs text-muted-foreground/60">·</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatStarredWhen(message.createdAt, message.time)}</span>
                </div>
                <p className="text-sm text-foreground line-clamp-2">{getMessagePreview(message)}</p>
                {message.attachmentUrl && message.type !== "text" && (
                  // A link rather than a handler: a document or a full-size image
                  // belongs in its own tab, and the browser already knows how.
                  <AttachmentLink
                    href={message.attachmentUrl}
                    onClick={e => e.stopPropagation()}
                    className="mt-1 inline-block text-xs text-primary hover:underline"
                  >
                    {message.type === "image" ? "View photo" : message.type === "video" ? "Play video" : "Open file"}
                  </AttachmentLink>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                {onJumpTo && (
                  <button
                    onClick={e => { e.stopPropagation(); onJumpTo(message.id) }}
                    className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                    title="Jump to message"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}
                {hasCopyableText(message) && (
                  <button
                    onClick={e => { e.stopPropagation(); void handleCopy(message) }}
                    className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                    title="Copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={e => { e.stopPropagation(); onUnstar(message) }}
                  className="p-1.5 rounded-lg hover:bg-amber-400/10 text-muted-foreground hover:text-amber-500 transition-colors"
                  title="Unstar"
                >
                  <StarOff className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
